import escapedString from 'escape-string-regexp';
import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import { z } from 'zod';
import type { Shippers } from 'src/apis/capacity-management/types';
import { SearchableMultiSelect } from 'src/components/form/select/searchable-multi-select';
import { formOptionSchema } from 'src/components/form/zod-schemas';
import { useAxios } from 'src/hooks/use-axios';
import { useSearch } from 'src/hooks/use-search';
import type { Organizations } from 'src/pages/shippers/upload-documents/types';

type Props = {
  label: string;
  url: string;
  placeholder: string;
  name?: string;
};

const shipperMetaSchema = z.object({ eic: z.string() });

export const shipperOptionSchema = formOptionSchema.merge(shipperMetaSchema);

type ShipperMetaOptions = z.input<typeof shipperOptionSchema>;

export const ShipperSearchableMultiSelect: FC<Props> = ({
  label,
  url,
  placeholder,
  name = 'shipperId',
}) => {
  const [searchValue, setSearchValue, searchQuery] = useSearch('', {
    minLength: 1,
  });

  const searchRequest = useAxios(
    (axios, baseConfig) =>
      axios.request<Shippers | Organizations>({
        ...baseConfig,
        url,
      }),
    { neededOnPageLoad: false }
  );

  // on the very first search input we try to fetch the initial list
  useEffect(() => {
    if (!searchQuery || searchRequest.response) return;
    searchRequest.execute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, searchRequest.response]);

  const searchResult = useMemo(() => {
    if (!searchQuery || !searchRequest.response) return;

    const escapedSearchQuery = new RegExp(
      escapedString(searchQuery.toLowerCase())
    );

    return searchRequest.response.data._embedded.items
      .filter(
        (shipper) =>
          shipper.companyName.toLowerCase().match(escapedSearchQuery) ||
          shipper.eic.toLowerCase().match(escapedSearchQuery)
      )
      .map((shipper) => ({
        value: shipper.id,
        label: shipper.companyName,
        eic: shipper.eic,
      })) satisfies ShipperMetaOptions[];
  }, [searchQuery, searchRequest.response]);

  return (
    <SearchableMultiSelect
      label={label}
      name={name}
      searchValue={searchValue}
      setSearchValue={setSearchValue}
      searchValueForCurrentOptions={searchValue}
      pending={searchRequest.pending}
      options={searchResult}
      stacked
      placeholder={placeholder}
      renderOptionHint={(option) => option.eic}
      renderSelectedOptionTooltip={(option) => (
        <>
          <strong>EIC:</strong> {option.eic}
        </>
      )}
    />
  );
};
